import React, { useState, useEffect } from 'react';
import { FileText, X, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, collection, getDocs, deleteDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import UserDashboardSidebar from './UserDashboardSidebar';
import './SentRequests.css';

// Local Storage Helper
const LocalStorageHelper = {
    saveLoginData: (userId, email, name, userType = 'user') => {
        localStorage.setItem('isLoggedIn', 'true');
        localStorage.setItem('userId', userId);
        localStorage.setItem('userEmail', email);
        localStorage.setItem('userName', name);
        localStorage.setItem('userType', userType);
    },

    isLoggedIn: () => {
        return localStorage.getItem('isLoggedIn') === 'true';
    },

    getLoginData: () => {
        return {
            userId: localStorage.getItem('userId'),
            email: localStorage.getItem('userEmail'),
            name: localStorage.getItem('userName'),
            userType: localStorage.getItem('userType'),
        };
    },

    clearLoginData: () => {
        localStorage.removeItem('isLoggedIn');
        localStorage.removeItem('userId');
        localStorage.removeItem('userEmail');
        localStorage.removeItem('userName');
        localStorage.removeItem('userType');
    }
};

const SentRequests = () => {
    const navigate = useNavigate();
    const [sidebarVisible, setSidebarVisible] = useState(true);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    
    // Authentication state
    const [currentUser, setCurrentUser] = useState(null);
    const [userData, setUserData] = useState(null);
    const [userName, setUserName] = useState('User');
    const [profilePhoto, setProfilePhoto] = useState(null);
    
    // Sent requests state
    const [sentRequests, setSentRequests] = useState([]);
    const [loadingRequests, setLoadingRequests] = useState(true);
    
    // Request details modal state
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showRequestModal, setShowRequestModal] = useState(false);
    const [avatarLoadError, setAvatarLoadError] = useState(false);

    // Authentication check
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                setCurrentUser(user);
                await loadUserData(user.uid);
                await fetchSentRequests(user.uid);
            } else {
                navigate('/Login');
            }
        });

        return () => unsubscribe();
    }, [navigate]);

    // Load user data to get name and profile photo
    const loadUserData = async (userId) => {
        try {
            // Try users collection first
            let userDoc = await getDoc(doc(db, 'users', userId));
            if (!userDoc.exists()) {
                // Try photographers collection if not found in users
                userDoc = await getDoc(doc(db, 'photographers', userId));
            }

            if (userDoc.exists()) {
                const data = userDoc.data();
                setUserData(data);
                setUserName(data.name || data.displayName || 'User');
                setProfilePhoto(data.profileImage || data.avatar || null);
            } else {
                // Fallback to auth user data
                if (auth.currentUser) {
                    setUserName(auth.currentUser.displayName || 'User');
                }
            }
        } catch (error) {
            console.error('Error loading user data:', error);
            // Fallback to auth user data
            if (auth.currentUser) {
                setUserName(auth.currentUser.displayName || 'User');
            }
        }
    };

    // Fetch sent requests from all photographers' requests subcollections
    const fetchSentRequests = async (userId) => {
        try {
            setLoadingRequests(true);
            console.log('Fetching sent requests for user:', userId);
            
            // Get all photographers first
            const photographersSnapshot = await getDocs(collection(db, 'photographers'));
            const allRequests = [];
            
            // Check each photographer's requests subcollection for requests from this user
            for (const photographerDoc of photographersSnapshot.docs) {
                const photographerId = photographerDoc.id;
                const photographerData = photographerDoc.data();
                
                try {
                    const requestsRef = collection(db, 'photographers', photographerId, 'requests');
                    const requestsSnapshot = await getDocs(requestsRef);
                    
                    requestsSnapshot.forEach((requestDoc) => {
                        const requestData = requestDoc.data();
                        
                        // Only include requests from the current user
                        if (requestData.userId === userId) {
                            allRequests.push({
                                id: requestDoc.id,
                                photographerId: photographerId,
                                photographerName: requestData.photographerName || photographerData.name,
                                photographerImage: photographerData.profileImage || 'https://images.pexels.com/photos/1264210/pexels-photo-1264210.jpeg',
                                ...requestData
                            });
                        }
                    });
                } catch (error) {
                    console.error(`Error fetching requests from photographer ${photographerId}:`, error);
                }
            }
            
            // Sort by creation date (newest first)
            allRequests.sort((a, b) => {
                const dateA = a.createdAt?.toDate() || new Date(0);
                const dateB = b.createdAt?.toDate() || new Date(0);
                return dateB - dateA;
            });
            
            console.log('Fetched sent requests:', allRequests);
            setSentRequests(allRequests);
            
        } catch (error) {
            console.error('Error fetching sent requests:', error);
            toast.error('Failed to load sent requests');
        } finally {
            setLoadingRequests(false);
        }
    };

    // Cancel a sent request
    const handleCancelRequest = async (request, event) => {
        event.stopPropagation(); // Prevent card click
        
        try {
            console.log('Cancelling request:', request);
            
            // Delete the request from the photographer's requests subcollection
            await deleteDoc(doc(db, 'photographers', request.photographerId, 'requests', request.id));
            
            // Update local state
            setSentRequests(prev => prev.filter(r => r.id !== request.id));
            
            toast.success('Request cancelled successfully');
        } catch (error) {
            console.error('Error cancelling request:', error);
            toast.error('Failed to cancel request');
        }
    };

    // Handle card click to redirect to photographer portfolio
    const handleCardClick = (request) => {
        navigate(`/photographer/${request.photographerId}`);
    };

    // Handle view request details
    const handleViewRequest = (request, event) => {
        event.stopPropagation(); // Prevent card click
        setSelectedRequest(request);
        setShowRequestModal(true);
    };

    // Close request details modal
    const closeRequestModal = () => {
        setShowRequestModal(false);
        setSelectedRequest(null);
    };

    // Time ago helper function
    const timeAgo = (timestamp) => {
        if (!timestamp) return 'Recently';
        
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        const now = new Date();
        const seconds = Math.floor((now - date) / 1000);
        
        if (seconds < 60) return 'Just now';
        if (seconds < 3600) {
            const minutes = Math.floor(seconds / 60);
            return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
        }
        if (seconds < 86400) {
            const hours = Math.floor(seconds / 3600);
            return `${hours} hour${hours > 1 ? 's' : ''} ago`;
        }
        if (seconds < 604800) {
            const days = Math.floor(seconds / 86400);
            return `${days} day${days > 1 ? 's' : ''} ago`;
        }
        
        return date.toLocaleDateString();
    };

    // Format date for display
    const formatDate = (timestamp) => {
        if (!timestamp) return 'Date not available';
        
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // Format budget display
    const formatBudget = (budget) => {
        const budgetMap = {
            'under-25k': 'Under ₹25,000',
            '25k-50k': '₹25,000 - ₹50,000',
            '50k-1l': '₹50,000 - ₹1,00,000',
            '1l-2l': '₹1,00,000 - ₹2,00,000',
            'above-2l': 'Above ₹2,00,000'
        };
        return budgetMap[budget] || budget;
    };

    // Format time of day
    const formatTimeOfDay = (timeOfDay) => {
        const timeMap = {
            'morning': 'Morning (6 AM - 12 PM)',
            'afternoon': 'Afternoon (12 PM - 6 PM)',
            'evening': 'Evening (6 PM - 10 PM)',
            'flexible': 'Flexible'
        };
        return timeMap[timeOfDay] || timeOfDay;
    };

    // Format type of shoot
    const formatTypeOfShoot = (typeOfShoot) => {
        const typeMap = {
            'wedding': 'Wedding Photography',
            'portrait': 'Portrait Photography',
            'event': 'Event Photography',
            'fashion': 'Fashion Photography',
            'product': 'Product Photography',
            'corporate': 'Corporate Photography',
            'nature': 'Nature Photography',
            'other': 'Other'
        };
        return typeMap[typeOfShoot] || typeOfShoot;
    };

    // Handle logout
    const handleLogout = async () => {
        try {
            await signOut(auth);
            LocalStorageHelper.clearLoginData();
            navigate('/Login');
        } catch (error) {
            console.error('Error logging out:', error);
        }
    };

    // Handle home navigation
    const handleHomeNavigation = () => {
        navigate('/LandingPage');
        closeSidebar();
    };

    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            if (!mobile && !sidebarVisible) {
                setSidebarVisible(true);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [sidebarVisible]);

    // Reset the broken-image fallback whenever a new photo URL comes in
    useEffect(() => {
        setAvatarLoadError(false);
    }, [profilePhoto]);

    const toggleSidebar = () => {
        setSidebarVisible(!sidebarVisible);
    };

    const closeSidebar = () => {
        if (isMobile) {
            setSidebarVisible(false);
        }
    };

    return (
        <div className="sentreq-container">
            <UserDashboardSidebar
                activeItem="requests"
                visible={sidebarVisible}
                isMobile={isMobile}
                onToggle={toggleSidebar}
                onClose={closeSidebar}
                userName={userName}
                profilePhoto={profilePhoto}
                onHome={handleHomeNavigation}
                onLogout={handleLogout}
            />

            {/* Main Content */}
            <div className={`sentreq-main-content ${!sidebarVisible ? 'expanded' : ''}`}>
                <div className="sentreq-main-header">
                    <div className="sentreq-welcome-container">
                        <div className="sentreq-avatar-container">
                            <div className="sentreq-user-avatar-main">
                                {profilePhoto && !avatarLoadError ? (
                                    <img
                                        src={profilePhoto}
                                        alt={userName}
                                        className="sentreq-user-avatar-main-img"
                                        onError={() => setAvatarLoadError(true)}
                                    />
                                ) : (
                                    userName.charAt(0).toUpperCase()
                                )}
                            </div>
                        </div>
                        <div>
                            <h1 className="sentreq-welcome-heading">
                                Hello, <span className="sentreq-username-text">{userName}</span>
                                <span className="sentreq-wave-emoji">👋</span>
                            </h1>
                        </div>
                    </div>
                </div>

                {/* Sent Requests Section */}
                <div className="sentreq-section">
                    <h2 className="sentreq-section-title">
                        <FileText size={20} />
                        Your Sent Requests ({sentRequests.length})
                    </h2>
                    
                    {loadingRequests ? (
                        <div className="sentreq-loading">
                            <p>Loading your sent requests...</p>
                        </div>
                    ) : sentRequests.length > 0 ? (
                        <div className="sentreq-grid">
                            {sentRequests.map((request) => (
                                <div 
                                    key={request.id} 
                                    className="sentreq-card"
                                    onClick={() => handleCardClick(request)}
                                >
                                    <div className="sentreq-card-header">
                                        <div className="sentreq-studio-avatar">
                                            <img
                                                src={request.photographerImage}
                                                alt={request.photographerName}
                                                loading="lazy"
                                                decoding="async"
                                                className="sentreq-studio-avatar-img"
                                                onError={(e) => {
                                                    e.target.src = 'https://images.pexels.com/photos/1264210/pexels-photo-1264210.jpeg';
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <div className="sentreq-studio-name">
                                                {request.photographerName}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="sentreq-card-content">
                                        <div className="sentreq-status-info">
                                            <div className="sentreq-status-text">
                                                Request {request.status || 'sent'}
                                            </div>
                                            <div className="sentreq-time-ago">
                                                {timeAgo(request.createdAt)}
                                            </div>
                                        </div>
                                        <div className="sentreq-card-actions">
                                            <button 
                                                className="sentreq-action-btn sentreq-view-btn"
                                                onClick={(e) => handleViewRequest(request, e)}
                                            >
                                                <Eye size={14} />
                                                View Request
                                            </button>
                                            <button 
                                                className="sentreq-action-btn sentreq-cancel-btn"
                                                onClick={(e) => handleCancelRequest(request, e)}
                                            >
                                                Cancel Request
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="sentreq-no-requests">
                            <p>No sent requests found. Start browsing photographers to send your first request!</p>
                            <button 
                                className="sentreq-nav-button"
                                onClick={() => navigate('/BrowsePhotographer')}
                            >
                                Browse Photographers
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Request Details Modal */}
            {showRequestModal && selectedRequest && (
                <div className="sentreq-modal-overlay" onClick={closeRequestModal}>
                    <div className="sentreq-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="sentreq-modal-header">
                            <h2> Request Details</h2>
                            <button className="sentreq-modal-close" onClick={closeRequestModal}>
                                <X size={20} />
                            </button>
                        </div>
                        
                        <div className="sentreq-modal-body">
                            <div className="sentreq-photographer-info">
                                <img 
                                    src={selectedRequest.photographerImage}
                                    alt={selectedRequest.photographerName}
                                    className="sentreq-photographer-avatar"
                                    onError={(e) => {
                                        e.target.src = 'https://images.pexels.com/photos/1264210/pexels-photo-1264210.jpeg';
                                    }}
                                />
                                <div>
                                    <h3>{selectedRequest.photographerName}</h3>
                                    <p className="sentreq-status-badge">
                                        Status: {selectedRequest.status || 'Pending'}
                                    </p>
                                </div>
                            </div>
                            
                            <div className="sentreq-details-grid">
                                <div className="sentreq-detail-item">
                                    <label> Location:</label>
                                    <span>{selectedRequest.location}</span>
                                </div>
                                
                                <div className="sentreq-detail-item">
                                    <label> Preferred Date:</label>
                                    <span>{formatDate(selectedRequest.preferredDate)}</span>
                                </div>
                                
                                <div className="sentreq-detail-item">
                                    <label> Budget Range:</label>
                                    <span>{formatBudget(selectedRequest.budget)}</span>
                                </div>
                                
                                <div className="sentreq-detail-item">
                                    <label> Type of Shoot:</label>
                                    <span>{formatTypeOfShoot(selectedRequest.typeOfShoot)}</span>
                                </div>
                                
                                <div className="sentreq-detail-item">
                                    <label> Time of Day:</label>
                                    <span>{formatTimeOfDay(selectedRequest.timeOfDay)}</span>
                                </div>
                                
                                <div className="sentreq-detail-item">
                                    <label> Sent On:</label>
                                    <span>{formatDate(selectedRequest.createdAt)}</span>
                                </div>
                                
                                {selectedRequest.message && (
                                    <div className="sentreq-detail-item sentreq-message-item">
                                        <label> Message:</label>
                                        <span>{selectedRequest.message}</span>
                                    </div>
                                )}
                            </div>
                            
                            <div className="sentreq-modal-note">
                                <p>📌 <strong>Note:</strong> This request has been sent and cannot be edited. You can only cancel it if needed.</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SentRequests;
